import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';
import { SvgIcon } from 'ui/SvgIcon';
import ChevronDownIcon from 'ui/Icons/tabler/chevron-down.component.svg';
import ChevronLeftIcon from 'ui/Icons/tabler/chevron-left.component.svg';
import ChevronRightIcon from 'ui/Icons/tabler/chevron-right.component.svg';
import { fromYearMonthString, toYearMonthString } from './helpers';

export interface IMonthPickerProps {
  value?: string | null; // yyyy-MM
  onChange: (value: string) => void; // yyyy-MM
  minYear?: number;
  maxYear?: number;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  dropdownAlignment?: 'left' | 'right';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface IMonthButtonProps {
  monthLabel: string;
  isSelected: boolean;
  onSelect: () => void;
}

interface IYearNavButtonProps {
  IconComponent: React.FC<React.SVGProps<SVGSVGElement>>;
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
}

const YearNavButton: React.FC<IYearNavButtonProps> = ({ IconComponent, disabled, onClick, ariaLabel }) => {
  return (
    <button
      type="button"
      className={classNames('p-0 bg-transparent border-none cursor-pointer', {
        'opacity-40 cursor-not-allowed pointer-events-none': disabled,
      })}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <SvgIcon
        IconComponent={IconComponent}
        className="w-[12px] h-[12px] fill-transparent stroke-brown-100 stroke-2 relative top-[-2px] cursor-pointer"
      />
    </button>
  );
};

const MonthButton: React.FC<IMonthButtonProps> = ({ monthLabel, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      className={classNames('h-[35px] w-[40px] rounded border border-solid py-[5px] px-0 cursor-pointer', {
        'border-gray-20 bg-white': !isSelected,
        'border-teal-80 font-bold bg-green-25': isSelected,
      })}
      onClick={onSelect}
    >
      <span className="font-pt-sans text-[15px] leading-[21px] text-black">{monthLabel}</span>
    </button>
  );
};

export const MonthPicker: React.FC<IMonthPickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select month',
  disabled = false,
  className,
  inputClassName,
  dropdownClassName,
  dropdownAlignment = 'left',
  minYear,
  maxYear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const parsedValue = useMemo(() => fromYearMonthString(value), [value]);
  const selectedYear = parsedValue?.year;
  const selectedMonthIndex = parsedValue?.monthIndex;
  const [cursorYear, setCursorYear] = useState<number>(selectedYear ?? new Date().getFullYear());

  const { instanceClassname } = useHideOnClickOutside(setIsOpen, 'month-picker');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCursorYear(selectedYear ?? new Date().getFullYear());
  }, [isOpen, selectedYear]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const displayString = useMemo(() => {
    if (!parsedValue) {
      return placeholder;
    }
    return `${MONTHS[parsedValue.monthIndex]} ${parsedValue.year}`;
  }, [parsedValue, placeholder]);

  const canDecrementYear = minYear == null ? true : cursorYear > minYear;
  const canIncrementYear = maxYear == null ? true : cursorYear < maxYear;

  const handleToggle = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsOpen(prev => !prev);
  }, [disabled]);

  const handleYearDecrement = useCallback(() => {
    if (!canDecrementYear) {
      return;
    }
    setCursorYear(y => y - 1);
  }, [canDecrementYear]);

  const handleYearIncrement = useCallback(() => {
    if (!canIncrementYear) {
      return;
    }
    setCursorYear(y => y + 1);
  }, [canIncrementYear]);

  const handleSelectMonth = useCallback(
    (monthIndex: number) => {
      if (disabled) {
        return;
      }
      onChange(toYearMonthString(cursorYear, monthIndex));
      setIsOpen(false);
    },
    [cursorYear, disabled, onChange]
  );

  return (
    <div className={classNames(instanceClassname, 'font-pt-sans text-[14px] leading-19px relative', className)}>
      {label && <p className="m-0 mb-[5px] font-pt-sans text-[13px] text-black tracking-2xs">{label}</p>}

      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={classNames(
          'w-full flex items-center justify-between text-left h-[35px] border-1 border-gray-40',
          'bg-ivory',
          {
            'outline outline-2 outline-teal-100': isOpen,
            'cursor-pointer': !disabled,
            'cursor-not-allowed bg-gray-10 text-gray-100 border-gray-10': disabled,
          },
          inputClassName
        )}
      >
        <span
          className={classNames('px-10px truncate', {
            'text-gray-80 italic': !value,
          })}
        >
          {displayString}
        </span>

        <span className="border-l border-solid border-l-gray-20 flex h-full w-35px items-center justify-center">
          <SvgIcon
            IconComponent={ChevronDownIcon}
            className={classNames('w-4 h-4 fill-transparent stroke-gray-80 stroke-2 transition-rotate duration-500 ease-in-out', {
              'rotate-180': isOpen,
            })}
          />
        </span>
      </button>

      {isOpen && (
        <div
          className={classNames(
            'bg-ivory border-1 border-gray-40 absolute bottom-[-204px] z-50',
            dropdownClassName,
            {
              'left-0': dropdownAlignment === 'left',
              'right-0': dropdownAlignment === 'right',
            }
          )}
        >
          <div className="flex items-center justify-between px-[10px] py-[8px]">
            <YearNavButton
              IconComponent={ChevronLeftIcon}
              disabled={!canDecrementYear}
              onClick={handleYearDecrement}
              ariaLabel="Previous year"
            />

            <span className="label-year select-none font-bold text-[16px] leading-[22px] text-black">{cursorYear}</span>

            <YearNavButton
              IconComponent={ChevronRightIcon}
              disabled={!canIncrementYear}
              onClick={handleYearIncrement}
              ariaLabel="Next year"
            />
          </div>

          <div className="grid grid-cols-3 gap-[5px] p-[10px] pt-0">
            {MONTHS.map((monthLabel, monthIndex) => {
              const isSelected = selectedYear === cursorYear && selectedMonthIndex === monthIndex;
              return (
                <MonthButton
                  key={monthLabel}
                  monthLabel={monthLabel}
                  isSelected={isSelected}
                  onSelect={() => handleSelectMonth(monthIndex)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
